import { z } from 'zod';
import { zfd } from 'zod-form-data';
import {
    type InvoicePaymentPendingProposalStripeAttemptMutation,
    type InvoicePaymentPendingProposalStripeAttemptMutationVariables,
} from 'generated/graphql';
import { InvoicePaymentPendingProposalStripeAttemptDocument } from 'generated/documents/InvoicePaymentPendingProposalStripeAttemptDocument';
import type { ServerErrorResponse } from '@modules/root/graphql/responses';
import { SuccessResponse } from '@modules/root/graphql/responses';
import { UserErrorResponse } from '@modules/root/graphql/responses';
import type { ActionFunctionArgs } from '~/types/route-args';

export const INVOICE_PAYMENT_PENDING_PROPOSAL_STRIPE_ATTEMPT_API_ROUTE =
    '/remix-api/invoice-payment-pending-proposal-stripe-attempt';

const validator = zfd.formData({
    invoicePaymentId: zfd.text(
        z.string().min(1, 'Invoice Payment ID is required'),
    ),
    stripePaymentIntentId: zfd.text(z.string().optional()),
});

export type InvoicePaymentPendingProposalStripeAttemptAction =
    | SuccessResponse<{ id: number }>
    | UserErrorResponse
    | ServerErrorResponse;

/**
 * Creates a pending Stripe attempt before the customer confirms a PaymentIntent on the proposal page.
 */
export const action = async ({ request, context }: ActionFunctionArgs) => {
    const formData = await request.formData();
    const parsed = zfd.formData(validator).safeParse(formData);
    if (!parsed.success) {
        throw new UserErrorResponse(parsed.error);
    }

    const { invoicePaymentId, stripePaymentIntentId } = parsed.data;
    const result = await context.client.mutate<
        InvoicePaymentPendingProposalStripeAttemptMutation,
        InvoicePaymentPendingProposalStripeAttemptMutationVariables
    >({
        mutation: InvoicePaymentPendingProposalStripeAttemptDocument,
        variables: {
            InvoicePaymentId: parseInt(invoicePaymentId, 10),
            StripePaymentIntentId: stripePaymentIntentId || null,
        },
    });

    const invoicePayment =
        result.data?.invoicePaymentPendingProposalStripeAttempt;

    if (invoicePayment?.__typename === 'InvoicePayment') {
        return new SuccessResponse({
            id: invoicePayment.ID,
        });
    }

    return new UserErrorResponse(
        invoicePayment?.Message || 'Failed to pending proposal Stripe attempt',
    );
};
